package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.ANGOLA_LEGAL_ENTITY_ID
import com.solevo.dist.application.models.CreditInfo
import com.solevo.dist.application.models.User
import com.solevo.dist.application.models.User.Companion.legalId
import com.solevo.dist.application.models.Transaction
import com.solevo.dist.application.models.InvoiceStatus
import com.solevo.dist.network.api.user.UserRemoteSource
import com.solevo.dist.repository.ApplicationCustomerRepository
import com.solevo.dist.repository.ApplicationStatementRepository
import com.solevo.dist.i18n.Localization
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.AmountFormatter
import com.solevo.dist.util.StatusMapper
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import org.koin.mp.KoinPlatform.getKoin
import kotlin.math.absoluteValue

enum class StatementsTab(val label: String) { All("All"), Open("Open") }

data class StatementRowUi(
    val id: String,
    val label: String,
    val statusText: String,
    val statusColorHex: String,
    val dueDateText: String,
    val totalText: String,
)

data class CreditSummaryUi(
    val availableText: String = "—",
    val totalLineText: String = "—",
    val openInvoicesText: String = "—",
    val goodsInDeliveryText: String = "—"
)

data class StatementsUiState(
    val loading: Boolean = true,
    val error: String? = null,
    val selectedTab: StatementsTab = StatementsTab.All,
    val includeOpenOrders: Boolean = false,
    val header: CreditSummaryUi = CreditSummaryUi(),
    val rows: List<StatementRowUi> = emptyList(),
    val filterByDatesRange: Boolean = false,
    val startDate: LocalDate? = null,
    val endDate: LocalDate? = null
)

class StatementsViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val statementsRepository: ApplicationStatementRepository = getKoin().get()
    private val customerRepository: ApplicationCustomerRepository = getKoin().get()
    private val userRemoteSource: UserRemoteSource = getKoin().get()

    private val _state = MutableStateFlow(StatementsUiState())
    val state: StateFlow<StatementsUiState> = _state.asStateFlow()

    private var statementItems: List<StatementItem> = emptyList()
    private var userCredit: CreditInfo? = null

    @OptIn(ExperimentalCoroutinesApi::class)
    fun start() {
        _state.value = _state.value.copy(loading = true, error = null)

        scope.launch {
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest { user ->
                    if (user.customerId.isNotEmpty()) {
                        val creditInfoFlow = customerRepository.getCurrentCustomer()
                        val transactionsFlow = statementsRepository.getTransactionsLive()

                        creditInfoFlow
                            .combine(transactionsFlow) { customer, transactions ->
                                userCredit = customer?.creditInfo
                                val items = createStatementItems(transactions)
                                val sorted = items.sortedDescending()
                                statementItems = sorted
                                lastContent = StatementContent(
                                    creditInfo = customer?.creditInfo,
                                    statements = sorted.groupBy { it.date.startOfDay() },
                                    isPickupBlocked = customer?.isPickupBlocked ?: false,
                                    isCustomerBlocked = customer?.isBlocked ?: false
                                )
                                Unit
                            }
                    } else {
                        kotlinx.coroutines.flow.flow { }
                    }
                }
                .onEach { computeAndEmit() }
                .catch { t ->
                    _state.value = _state.value.copy(
                        loading = false,
                        error = t.message ?: "Unknown error",
                        rows = emptyList()
                    )
                }
                .collect()
        }
    }

    fun stop() = scope.cancel()

    fun selectTab(tab: StatementsTab) {
        if (_state.value.selectedTab == tab) return
        _state.value = _state.value.copy(selectedTab = tab)
        computeAndEmit()
    }

    fun setIncludeOpenOrders(enabled: Boolean) {
        _state.value = _state.value.copy(includeOpenOrders = enabled)
        computeAndEmit()
    }

    fun setStartDate(date: LocalDate?) {
        _state.value = _state.value.copy(startDate = date)
    }

    fun setEndDate(date: LocalDate?) {
        _state.value = _state.value.copy(endDate = date)
    }

    fun applyFilters(startDate: LocalDate?, endDate: LocalDate?) {
        _state.value = _state.value.copy(filterByDatesRange = true, startDate = startDate, endDate = endDate)
        computeAndEmit()
    }

    fun setFilterEnabled(enabled: Boolean) {
        _state.value = _state.value.copy(filterByDatesRange = enabled)
        computeAndEmit()
    }

    private fun computeAndEmit() {
        val tab = _state.value.selectedTab
        val base = statementItems
        val filteredTab = when (tab) {
            StatementsTab.All -> base
            StatementsTab.Open -> base.filter { it.transactionStatus is Transaction.Status.Open }
        }

        val filteredByDate = if (_state.value.filterByDatesRange) {
            filteredTab.filter { item ->
                val s = _state.value.startDate?.let { startOfDayInstant(it) }
                val e = _state.value.endDate?.let { endOfDayInstant(it) }
                val afterStart = s?.let { item.date >= it } ?: true
                val beforeEnd = e?.let { item.date <= it } ?: true
                afterStart && beforeEnd
            }
        } else filteredTab

        val rows = filteredByDate.map { it.toRow() }

        val header = buildCreditHeader(userCredit, _state.value.includeOpenOrders)
        val grouped = filteredByDate.groupBy { it.date.startOfDay() }

        lastContent = lastContent?.copy(statements = grouped)

        _state.value = _state.value.copy(
            loading = false,
            error = null,
            rows = rows,
            header = header
        )
    }

    /* ---------------- Mapping helpers ---------------- */

    private fun StatementItem.toRow(): StatementRowUi {
        val strings = Localization.strings.value
        val label = when (type) {
            is Transaction.Type.Invoice -> (invoiceNumber ?: voucherId).let { "${strings.wordInvoice} $it" }
            is Transaction.Type.CreditNote -> "${strings.wordCreditNote} ${id}"
            is Transaction.Type.Payment -> "${strings.wordPayment} ${voucherId}"
            is Transaction.Type.Other -> id
            is Transaction.Type.Reimbursement -> id
        }

        val (statusText, statusColorHex) = when (type) {
            is Transaction.Type.Invoice -> {
                val statusInfo = StatusMapper.mapInvoiceStatus(invoiceStatus ?: InvoiceStatus.Closed)
                statusInfo.label to statusInfo.colorHex
            }
            else -> when (transactionStatus) {
                is Transaction.Status.Closed -> {
                    val statusInfo = StatusMapper.mapTransactionStatus(transactionStatus)
                    statusInfo.label to statusInfo.colorHex
                }
                is Transaction.Status.Open -> {
                    val statusInfo = StatusMapper.mapTransactionStatus(transactionStatus)
                    statusInfo.label to statusInfo.colorHex
                }
                else -> "" to "#000000"
            }
        }

        val due = if (type is Transaction.Type.Invoice) (dueDate ?: date) else null
        val dueText = DateFormatter.formatDateShort(due)

        val amountForDisplay = when (type) {
            is Transaction.Type.Invoice -> totalPrice
            is Transaction.Type.CreditNote -> totalPrice
            is Transaction.Type.Payment -> totalPrice
            else -> totalPrice
        }
        val totalText = AmountFormatter.formatAmountWithPositiveSign(amountForDisplay)

        return StatementRowUi(
            id = id,
            label = label,
            statusText = statusText,
            statusColorHex = statusColorHex,
            dueDateText = dueText,
            totalText = totalText
        )
    }

    private fun buildCreditHeader(credit: CreditInfo?, includeOpenOrders: Boolean): CreditSummaryUi {
        if (credit == null) return CreditSummaryUi()
        val available = if (includeOpenOrders) credit.availableCreditAfterOpenOrders else credit.availableCredit
        return CreditSummaryUi(
            availableText = AmountFormatter.formatAmountNoSign(available),
            totalLineText = AmountFormatter.formatAmountNoSign(credit.totalCreditLine),
            openInvoicesText = AmountFormatter.formatAmountNoSign(credit.openInvoices),
            goodsInDeliveryText = AmountFormatter.formatAmountNoSign(credit.goodsInDelivery)
        )
    }

    private fun startOfDayInstant(d: LocalDate): Instant {
        val ldt = LocalDateTime(d.year, d.monthNumber, d.dayOfMonth, 0, 0, 0)
        return ldt.toInstant(TimeZone.currentSystemDefault())
    }

    private fun endOfDayInstant(d: LocalDate): Instant {
        val ldt = LocalDateTime(d.year, d.monthNumber, d.dayOfMonth, 23, 59, 59)
        return ldt.toInstant(TimeZone.currentSystemDefault())
    }

    private fun Instant.startOfDay(): Instant {
        val ldt = this.toLocalDateTime(TimeZone.currentSystemDefault())
        val start = LocalDateTime(ldt.year, ldt.monthNumber, ldt.dayOfMonth, 0, 0, 0)
        return start.toInstant(TimeZone.currentSystemDefault())
    }

    private data class StatementContent(
        val creditInfo: CreditInfo? = null,
        val statements: Map<Instant, List<StatementItem>>,
        val isPickupBlocked: Boolean = false,
        val isCustomerBlocked: Boolean = false,
    )

    private var lastContent: StatementContent? = null

    /* ---------------- Statements model helpers (aligned with Android/shared) ---------------- */

    private data class StatementItem(
        val id: String,
        val voucherId: String,
        val invoiceId: String? = null,
        val invoiceNumber: String? = null,
        val type: Transaction.Type,
        val dueDate: Instant? = null,
        val invoiceStatus: InvoiceStatus? = null,
        val transactionType: Transaction.Type? = null,
        val transactionStatus: Transaction.Status? = null,
        val date: Instant,
        val totalPrice: Double,
        val remaining: Double,
        val unit: String? = null,
        val currency: String,
    ) : Comparable<StatementItem> {
        override fun compareTo(other: StatementItem): Int {
            return compareValuesBy(this, other, { it.date }, { it.id })
        }
    }

    private fun createStatementItems(transactions: List<Transaction>): List<StatementItem> {
        return transactions.map { tx ->
            StatementItem(
                id = tx.transactionId,
                voucherId = tx.voucherId?.takeIf { it.isNotEmpty() } ?: tx.transactionId,
                type = tx.type,
                date = tx.date,
                totalPrice = tx.originalAmount,
                remaining = tx.remainingAmount,
                currency = tx.currency,
                transactionType = tx.type,
                transactionStatus = tx.status,
                invoiceStatus = tx.invoiceStatus,
                invoiceId = tx.invoiceId,
                invoiceNumber = getInvoiceNumberByLegalEntityId(tx),
                dueDate = tx.invoiceDueDate
            )
        }
    }

    private fun getInvoiceNumberByLegalEntityId(transaction: Transaction): String {
        return when {
            legalId.equals(ANGOLA_LEGAL_ENTITY_ID, ignoreCase = true) -> transaction.invoiceNumber.orEmpty()
            else -> transaction.voucherId.takeIf { !it.isNullOrBlank() } ?: transaction.transactionId
        }
    }
}


